import FluxogramSelector from 'components/Buttons/ButtonRadioDialog';
import RmFlowsDetailPanel from 'components/DetailPanels/RmFlowsDetailPanel';
import RMProductsDetailPanel from 'components/DetailPanels/RMProductsDetailPanel';
import ShipmentsDetailPanel from 'components/DetailPanels/ShipmentsDetailPanel';
import SubstancesDetailPanel from 'components/DetailPanels/SubstanceDetailPanel';
import BaseTable from 'components/Tables/BaseTable';
import { actions as mixtureActions } from 'ducks/mixtures';
import { actions as rawMaterialActions } from 'ducks/rawMaterials';
import { sortBy } from 'lodash';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import docDefinition from 'reports/flows/rawMaterialsSteps';
import {
  filterByFunctionSelector, groupBy, mixtureSubstancesByMixture, rawMaterialIdsByFlow,
  shipmentsByRawMaterial
} from 'selectors/generic';
import theme from 'source/theme';
import { DISTRIBUIDOR } from 'utils/constants/reach_role';
import tableIcons from 'utils/icons/materialTableIcons';
import columns from 'utils/tables/columns/inventory/rawMaterials';
import noIcon from 'utils/tables/noIcon';
import { currentUser } from 'selectors/currentUser';

pdfMake.addVirtualFileSystem(pdfFonts);

// `rm.products` was the list of product ids the raw material appears in; the
// composition rows name both sides, so they answer the same question - and the
// only thing asked of it here is whether there is one.
const productRawMaterialsByRawMaterial = groupBy('productRawMaterials', 'raw_material_id');

const Component = props => {
  let {
    filteredRawMaterials, flowId, flows, mixtureSubstancesByMixture,
    productRawMaterialsByRawMaterial, productsById, rawMaterialIdsOfFlow, rawMaterialsById,
    shipmentsByRawMaterial, stepRawMaterials, suppliersById, user
  } = props;
  const { setFlowId, setRawMaterial } = props;
  const tableRef = React.useRef(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [stepRawMPsByMP, setStepRawMPsByMP] = useState({});

  useEffect(() => {
    setSelectedIds(rawMaterialIdsOfFlow[flowId] || []);
    setStepRawMPsByMP(Object.groupBy(stepRawMaterials, s => s.raw_material_id));
  }, [flowId, rawMaterialIdsOfFlow, stepRawMaterials]);

  // TODO Adding Checked info to table, there should be a better way
  const rows = filteredRawMaterials
    .filter(substance => substance.hazardous &&
      (shipmentsByRawMaterial[substance.id] || [])
        .find(shipment => shipment.reach_role !== DISTRIBUIDOR))
    .map(subst =>
      selectedIds.includes(subst.id) ?
        { ...subst, tableData: { checked: true } } :
        { ...subst, tableData: { checked: false } }
    );
  const reOrderedRows = sortBy(rows, [row => !selectedIds.includes(row.id), 'id']);

  return (
    <BaseTable
      ref={tableRef}
      title={
        <FluxogramSelector
          buttonText="Fluxograma: "
          currentOption={flows.models[flowId]}
          header="Seleccione um fluxograma"
          optionKey={['code', 'name']}
          options={flows.data}
          onSelection={id => setFlowId(id)}
        />
      }
      columns={columns(user)}
      data={reOrderedRows}
      order={false}
      onRowClick={() => null}
      onRowDoubleClick={rowData => setRawMaterial(rowData)}
      options={{
        selection: true,
        showTextRowsSelected: false,
        showSelectAllCheckbox: false,
        selectionProps: rowData => ({ disabled: true }),
        rowStyle: rowData => ({
          backgroundColor: (!!stepRawMPsByMP[rowData.id] &&
            stepRawMPsByMP[rowData.id].some( srm => !srm.descriptors || srm.descriptors.length === 0)) ?
            theme.palette.rowErrorBackground.main : " "
        })
      }}
      actions={[
        {
          icon: tableIcons.Steps,
          tooltip: 'Gerir condições',
          onClick: (event, rowData) => setRawMaterial(rowData),
          position: 'row'
        },
        {
          //isFreeAction: true,
          //position: 'toolbar',
          icon: tableIcons.Pdf,
          tooltip: 'Relatório PDF',
          onClick: (event, rowData) => {
            const onlySelectedRMwithLCS = tableRef.current.state.data.filter(rm => rm.tableData.checked);
            pdfMake.createPdf(docDefinition(
              onlySelectedRMwithLCS,
              flows.models[flowId],
              stepRawMaterials
            ))
              .open();
          }
        }
      ]}
      onSelectionChange={(rows) => {
        const nextRowIds = rows.map(row => row.id);
        setSelectedIds(nextRowIds);
      }}
      detailPanel={[
        rowData => {
          const substances = mixtureSubstancesByMixture[rowData.id] || [];

          return substances.length === 0 ?
            noIcon :
            {
              icon: tableIcons.Substance,
              tooltip: 'Composição',
              render: () => <SubstancesDetailPanel rowData={substances} data={rawMaterialsById}
                                                   title={'Substâncias'}/>
            };
        },
        rowData => {
          // Same list as above, from the same index: a flow's raw materials are
          // its steps' step_raw_materials. This one sits inside a detailPanel,
          // which material-table calls for every row on every render - so it ran
          // before anything else noticed the field was gone, and took the screen
          // with it. There is no ErrorBoundary; the page just goes white.
          const rmFlows = flows.data.filter(
            flow => (rawMaterialIdsOfFlow[flow.id] || []).includes(rowData.id));

          if (rmFlows.length === 0) {
            return {
              disabled: true,
              icon: tableIcons.FlowsBase(theme.palette.error.main),
              tooltip: 'Fluxogramas',
              render: () => {
              }
            };
          }

          return {
            icon: tableIcons.FlowsBase(),
            tooltip: 'Fluxogramas',
            render: () => <RmFlowsDetailPanel rowData={rmFlows} title={'Fluxogramas'}/>
          };
        },
        rowData =>
          ({
            icon: (shipmentsByRawMaterial[rowData.id] || []).length > 0 ?
              tableIcons.Shipments :
              tableIcons.ShipmentsStyled({ htmlColor: theme.palette.error.main }),
            tooltip: 'Fornecimentos',
            render: () => <ShipmentsDetailPanel rowData={rowData} suppliersById={suppliersById}
                                                rawMaterialsById={rawMaterialsById} title={'Fornecimentos'}/>
          }),
        rowData =>
          ({
            icon: (productRawMaterialsByRawMaterial[rowData.id] || []).length > 0 ?
              tableIcons.Products :
              tableIcons.ProductsStyled({ htmlColor: theme.palette.error.main }),
            tooltip: 'Produtos',
            render: () =>
              <RMProductsDetailPanel
                rowData={rowData}
                productsById={productsById}
                rawMaterialsById={rawMaterialsById}
                title={'Produtos'}
              />
          })
      ]}
    />
  );
};

const makeMapStateToProps = () => {
  const filteredRawMaterials = filterByFunctionSelector('rawMaterials', rm => rm.code !== null && rm.code !== '');
  const mapStateToProps = (state, props) => ({
    rawMaterialIdsOfFlow: rawMaterialIdsByFlow(state),
    filteredRawMaterials: filteredRawMaterials(state),
    flows: state.flows,
    mixtureSubstancesByMixture: mixtureSubstancesByMixture(state),
    productRawMaterialsByRawMaterial: productRawMaterialsByRawMaterial(state),
    shipmentsByRawMaterial: shipmentsByRawMaterial(state),
    stepRawMaterials: state.stepRawMaterials.data,
    user: currentUser(state),
    rawMaterialsById: state.rawMaterials.models,
    suppliersById: state.suppliers.models,
    productsById: state.products.models
  });
  return mapStateToProps;
};

const mapDispatchToProps = dispatch => ({
  fetchRawMaterial: id => dispatch(rawMaterialActions.fetchOne(id)),
  create: data => dispatch(mixtureActions.createPromise(data)),
  remove: id => dispatch(mixtureActions.removePromise(id)),
  update: (id, data) => dispatch(mixtureActions.updatePromise(id, data))
});

export default connect(makeMapStateToProps, mapDispatchToProps)(Component);
